import React, { useContext, useEffect, useMemo, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min";
import { Icon } from "@iconify/react";
import { Link, useNavigate } from "react-router-dom";
import { ConfigContext } from "../context/GlobalContext";
import PaginationView from "./PaginationView";
import { FilterdData } from "../helper/FilterdData";
import GetSoldItemsData from "../data/Comman/GetSoldItesmData";
import DataNotFoundView from "./DataNotFoundView";
import * as actionType from '../context/actions';
import { Languages } from "../Utils/Languages";
import { resolveAuditName } from "../Utils/audit_helper";
import BusinessDropdown from "./child/BusinessDropdown";
import Currancy from "../Utils/currency.json";

const SoldItemsTableDataLayer = ({ SoldItemsData, selectedBusiness: propSelectedBusiness, allAllowedIds, searchText = "", selectedCurrency }) => {

    const navigate = useNavigate();
    const { state, dispatch } = useContext(ConfigContext);
    const [matchedItems, setMatchedItems] = useState(SoldItemsData);
    // ✅ Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);

    const GetSoldItems = async () => {
        // Apply filter first
        let filtersolditems = FilterdData(state, state?.SOLDITEMS, "date");

        if (selectedCurrency && selectedCurrency !== "All") {
            filtersolditems = filtersolditems.filter(item => item?.currency === selectedCurrency);
        }

        let itemList = [];
        if (propSelectedBusiness !== "null" && propSelectedBusiness != null) {
            itemList = filtersolditems
                ?.filter(item => item?.companyId == propSelectedBusiness)
                ?.map((item, index) => ({
                    ...item,
                    srNo: index + 1
                }));
        } else {
            itemList = filtersolditems
                ?.filter(item => allAllowedIds?.includes(String(item?.companyId)))
                ?.map((item, index) => ({
                    ...item,
                    srNo: index + 1
                }));
        }

        setMatchedItems(itemList);
    };


    useEffect(() => {
        const allTransactions = [
            ...(state?.INVOICES || []),
        ];
        GetSoldItemsData(state, dispatch, allTransactions);
    }, [state?.INVOICES, state?.QUOTATION, state?.PROFORMAINVOICES, state?.SALERETURN, dispatch]);

    // 🔹 Apply filtering only when sold items, selected business, or selected currency changes
    useEffect(() => {
        GetSoldItems();
    }, [state?.ACTIVEFILTER, propSelectedBusiness, state?.SOLDITEMS, allAllowedIds, selectedCurrency]);


    // ✅ Handle Pagination Data
    const totalPages = Math.ceil(matchedItems?.length / itemsPerPage);
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        const data = matchedItems?.slice(start, start + itemsPerPage) || [];

        // Append a new row (Example: Total Row or Custom Data)
        return [...data]

    }, [matchedItems, currentPage, itemsPerPage]);

    // ✅ Sort Caret Function
    const sortCaret = (order) => (
        <span className="d-inline-flex align-items-center ms-2">
            {!order ? (
                <Icon icon="mdi:swap-vertical" className="text-muted" />
            ) : order === "asc" ? (
                <Icon icon="mdi:arrow-up-thick" className="text-muted" />
            ) : (
                <Icon icon="mdi:arrow-down-thick" className="text-muted" />
            )}
        </span>
    );
    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
        }
    }, [totalPages, currentPage]);
    const { SearchBar } = Search;

    // Helper to format currency footer
    const formatFooter = (columnData) => {
        const total = columnData.reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
        
        if (selectedCurrency && selectedCurrency !== "All") {
            const matchedCurrency = Currancy.CountryCurrencyResponse.find(
                (c) => c.currencyCode === selectedCurrency
            );
            if (matchedCurrency) {
                const code = matchedCurrency.currencyCode;
                const symbol = matchedCurrency.currencytSymbol;
                return `${symbol === "$" ? `${code} ${symbol} ` : `${symbol} `} ${parseFloat(total).toFixed(2)}`;
            }
        }
        
        return `${state?.SYMBOL === "$" ? `${state?.CURRENCYCODE} ${state?.SYMBOL} ` : `${state?.SYMBOL} `} ${parseFloat(total).toFixed(2)}`;
    };

    // ✅ BootstrapTable Columns
    const columns = [
        {
            dataField: 'srNo',
            text: Languages[state?.LANGUAGE]?.s_r,
            headerAlign: "center",
            align: "center",
            sort: true,
            sortCaret: sortCaret,
            footer: Languages[state?.LANGUAGE]?.total,
            footerAlign: "center",
            footerClasses: paginatedData.length > 0 ? "custom-table-footer" : "custom-table-footer-hiden",
        },

        {
            dataField: 'name',
            text: Languages[state?.LANGUAGE]?.items,
            sort: true,
            sortCaret: sortCaret,
            formatter: (cell, row) => {
                const business = state?.BUSINESSES?.find(
                    b => String(b.companyId) === String(row.companyId)
                );

                const isSharedBusiness = business?.isShared === true;

                const currentUserId = state?.USERID;
                const currentUserStr =
                    state?.SET_USER?.name ||
                    state?.SET_USER?.email ||
                    state?.SET_USER?.contact;

                const resolvedName = resolveAuditName(row.createdBy, state.membersData);

                const isCurrentUserCreator =
                    (typeof row.createdBy === "object" && row.createdBy?.uid === currentUserId) ||
                    (typeof row.createdBy === "string" && row.createdBy === currentUserId) ||
                    resolvedName === currentUserStr;

                let displayName = null;

                const showByLabel = row.createdBy
                    ? !isCurrentUserCreator   // ✅ show only if NOT current user
                    : (isSharedBusiness && business?.ownerName); // ✅ fallback only for shared

                if (showByLabel) {
                    displayName = row.createdBy
                        ? resolvedName
                        : business?.ownerName;
                }

                return (
                    <div>
                        <div>{cell || "Unknown"}</div>

                        {displayName && (
                            <small
                                className="text-secondary-light"
                                style={{ fontSize: "11px", display: "block", marginTop: "-4px" }}
                            >
                                by: {displayName}
                            </small>
                        )}
                    </div>
                );
            },
            footer: "",
            footerAlign: "center",
            footerClasses: paginatedData.length > 0 ? "custom-table-footer" : "custom-table-footer-hiden",
        },
        {
            dataField: 'quantity',
            text: Languages[state?.LANGUAGE]?.quantity,
            headerAlign: "center",
            align: "center",
            sort: true,
            sortCaret: sortCaret,
            footerAlign: "center",
            footer: columnData => columnData.reduce((sum, row) => sum + parseFloat(row), 0),
            footerClasses: paginatedData.length > 0 ? "custom-table-footer" : "custom-table-footer-hiden",
        },
        {
            dataField: 'price',
            text: Languages[state?.LANGUAGE]?.price,
            formatter: (cell, row) => {
                const matchedCurrency = row?.currency ? Currancy.CountryCurrencyResponse.find(
                    (c) => c.currencyCode === row.currency
                ) : null;
                const currencyCode = matchedCurrency ? matchedCurrency.currencyCode : state?.CURRENCYCODE;
                const currencySymbol = matchedCurrency ? matchedCurrency.currencytSymbol : state?.SYMBOL;
                return (
                    <p className="text-md mb-0 fw-medium flex-grow-1 text-wrap">
                        {currencySymbol === "$" ? `${currencyCode} ${currencySymbol} ` : `${currencySymbol} `} {cell}
                    </p>
                );
            },
            sort: true,
            style: { minWidth: "120px" },
            sortCaret: sortCaret,
            footer: columnData => formatFooter(columnData),
            footerClasses: paginatedData.length > 0 ? "custom-table-footer" : "custom-table-footer-hiden",
        },
        {
            dataField: 'total_amount',
            text: Languages[state?.LANGUAGE]?.total_amount,
            formatter: (cell, row) => {
                const matchedCurrency = row?.currency ? Currancy.CountryCurrencyResponse.find(
                    (c) => c.currencyCode === row.currency
                ) : null;
                const currencyCode = matchedCurrency ? matchedCurrency.currencyCode : state?.CURRENCYCODE;
                const currencySymbol = matchedCurrency ? matchedCurrency.currencytSymbol : state?.SYMBOL;
                return (
                    <p className="text-md mb-0 fw-medium flex-grow-1 text-wrap">
                        {currencySymbol === "$" ? `${currencyCode} ${currencySymbol} ` : `${currencySymbol} `} {cell}
                    </p>
                );
            },
            sort: true,
            style: { minWidth: "150px" },
            sortCaret: sortCaret,
            footer: columnData => formatFooter(columnData),
            footerClasses: paginatedData?.length > 0 ? "custom-table-footer" : "custom-table-footer-hiden",
        }
    ];
    // ✅ Handle Items Per Page Selection
    const handleSelection = (val) => {
        setItemsPerPage(val);
        setCurrentPage(1)
    };

    return (
        <div className="card">
            <div className="card-body">
                <ToolkitProvider keyField="srNo" data={matchedItems} columns={columns} search>
                    {props => {
                        const lowerSearch = searchText?.toLowerCase() || "";
                        const filteredData = matchedItems.filter(item =>
                            item.name?.toLowerCase()?.includes(lowerSearch) ||
                            item.invoiceNumber?.toLowerCase()?.includes(lowerSearch) ||
                            item.customerName?.toLowerCase()?.includes(lowerSearch) ||
                            item.price?.toString()?.toLowerCase().includes(lowerSearch) ||
                            item.total_amount?.toString()?.toLowerCase().includes(lowerSearch) ||
                            item.quantity?.toString()?.toLowerCase().includes(lowerSearch)
                        );

                        const start = (currentPage - 1) * itemsPerPage;
                        const paginatedSearchData = filteredData.slice(start, start + itemsPerPage);

                        return (
                            <>


                                <div className="table-responsive">
                                    <BootstrapTable
                                        {...props.baseProps}
                                        bootstrap4
                                        bordered={false}
                                        data={paginatedSearchData}
                                        wrapperClasses={`custom-table-border invoice-table ${localStorage.getItem("theme") === "dark" ? "table-dark" : "table-light"}`}
                                        noDataIndication={() => <DataNotFoundView height={400} />}
                                        rowStyle={{ cursor: "pointer" }}
                                    />
                                </div>

                                {filteredData.length > 0 && (
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div className="d-flex align-items-center gap-2 entry_per_page">
                                            <div className="dropdown">
                                                <button className="btn btn-outline-primary-600 not-active px-18 py-6 dropdown-toggle toggle-icon" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                                    {itemsPerPage}
                                                </button>
                                                <ul className="dropdown-menu shadow-lg">
                                                    {[5, 10, 15, 20].map(value => (
                                                        <li key={value}>
                                                            <Link
                                                                className={`dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 ${itemsPerPage === value ? "bg-primary-50" : ""}`}
                                                                onClick={() => handleSelection(value)}
                                                            >
                                                                {value}
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <span className="text-secondary-light">{Languages[state?.LANGUAGE]?.entries_per_page}</span>
                                        </div>

                                        <div className="ms-auto">
                                            <PaginationView
                                                currentPage={currentPage}
                                                totalPages={Math.ceil(filteredData.length / itemsPerPage)}
                                                handlePageChange={setCurrentPage}
                                            />
                                        </div>
                                    </div>
                                )}
                            </>
                        );
                    }}
                </ToolkitProvider>
            </div>
        </div>
    );
};

export default SoldItemsTableDataLayer;
